/**
 * Балк-планирование лидов/маркетинга для прототип-сетки (P2-2).
 *
 * Отличие от боевого BulkLeadPlanModal — семантика «на оставшиеся недели» (решение 2026-06-28):
 * нет выбора недели; недельная ставка ложится flat на КАЖДУЮ оставшуюся редактируемую неделю
 * (`editableWeekNumbers`) по всем студиям партнёра по %. Завершённые недели не трогаются.
 * Балк остаётся top-down %-аллокатором (студии индивидуально не правят), бюджет и лиды делятся
 * одним %. Плюс «авто-% по исторической доле лидов» (предзаполняет % из факта прошлых ~3 мес).
 */
import { useEffect, useMemo, useState } from 'react'
import { AlertTriangle, Shuffle, History, X, ArrowLeft, ArrowRight, Check, ChevronDown, RotateCcw, HelpCircle } from 'lucide-react'
import { Studio } from '@/types'
import { getSupabase } from '@/lib/supabase'
import { NumberInput } from '@/components/ui/FormComponents'
import { formatCurrency } from '@/lib/utils'
import { OnboardingTour, type TourStep } from '@/components/onboarding/OnboardingTour'
import { useOnboardingTour } from '@/components/onboarding/useOnboardingTour'

// ── Мини-гид по модалке ─────────────────────────────────────────────────────────
// Объясняет первый шаг мастера («Ввод значений») и общий принцип — гид только
// показывает, поэтому листать визард не пытается; шаги 2–3 описаны на степпере.
// Ссылка стабильна (модульный уровень) — её фильтрует OnboardingTour.
const BULK_LEADS_TOUR_STEPS: TourStep[] = [
  {
    title: 'Распределение лидов и маркетинга',
    body: 'Здесь общий план лидов и бюджета задаётся сразу для нескольких студий франчайзи и раскладывается по долям. Это мастер из 3 шагов — покажем первый и общий принцип.',
  },
  {
    target: 'leads-stepper',
    title: 'Три шага мастера',
    body: '1) Ввод общих значений · 2) Распределение по студиям в процентах · 3) Проверка и сохранение. Сейчас вы на первом шаге. Завершённые недели периода ни на одном шаге не меняются.',
  },
  {
    target: 'leads-cities',
    title: 'Города',
    body: 'Если у франчайзи несколько городов — выберите, на какие студии распределять план. Когда город один (вся сеть франчайзи), этого выбора нет.',
  },
  {
    target: 'leads-basis',
    title: 'Как вводить план',
    body: 'Переключатель базы ввода: «за неделю» — недельная ставка, либо «за оставшиеся недели» — сумма на весь остаток периода. Рядом — «Сбросить ввод» к текущему сохранённому плану.',
  },
  {
    target: 'leads-inputs',
    title: 'Лиды и бюджет',
    body: 'Введите общий план лидов и маркетингового бюджета на всю выбранную группу студий. Под полями — автопересчёт второй базы и подсказки из истории (план / факт / 3 мес).',
  },
  {
    target: 'leads-summary',
    title: 'Что получится',
    body: 'Сводка: сколько ляжет в каждую неделю, итог за оставшиеся недели и стоимость лида (бюджет ÷ лиды). Лиды влияют на воронку и плановую выручку.',
  },
  {
    target: 'leads-next',
    title: 'Дальше — распределение',
    body: 'Кнопка «Далее» ведёт на шаг распределения по студиям: доли (%) можно задать вручную, кнопкой «Распределить остаток» или авто — «По исторической доле лидов». Затем — проверка и сохранение.',
  },
]

type WeeklyPlanMode = 'auto' | 'manual'
type WeeklyPlanSource = 'first_week' | 'remaining_split' | 'avg_fact' | 'manual' | 'partner_allocation' | null

interface Props {
  isOpen: boolean
  onClose: () => void
  onSaved?: () => void
  studios: Studio[]
  currentPartnerNumber: number | null
  currentPeriodId: number | null
  currentYearNumber: number | null
  currentMonthNumber: number | null
  /** Оставшиеся редактируемые недели периода (в начале = все; в середине = будущие + текущая в grace). */
  editableWeekNumbers: number[]
  /** Старт выбранного периода (ISO) — точка отсчёта окна истории для авто-%. */
  periodStart: string
  periodLabel?: string
  userId?: string | null
  currency?: string
  /** «Сегодня» из сетки (возможна симуляция SIMULATE_TODAY_MID_PERIOD) — для факта/прогноза. */
  today: string
  /** Длина периода в днях (28/35) — для прогноза по темпу. */
  periodLenDays: number
}

interface ExistingPlanRow {
  studio_internal_id: number
  week_number_in_period: number
  cost_per_lead_plan?: number | null
  leads_count_plan?: number | null
  marketing_budget_plan?: number | null
  booking_conversion_plan?: number | null
  appointments_count_plan?: number | null
  visit_conversion_plan?: number | null
  new_visitors_count_plan?: number | null
  sales_conversion_plan?: number | null
  new_sales_count_plan?: number | null
  new_sales_aov_plan?: number | null
  new_sales_amount_plan?: number | null
  new_subscriptions_conversion_plan?: number | null
  new_subscriptions_count_plan?: number | null
  new_subscriptions_aov_plan?: number | null
  new_subscriptions_amount_plan?: number | null
  membership_fees_new_plan?: number | null
  gross_revenue_plan?: number | null
  plan_mode?: WeeklyPlanMode | null
  plan_source?: WeeklyPlanSource
}

interface AllocationTemplateRow {
  studio_internal_id: number
  allocation_percent: number | null
  is_enabled: boolean | null
  allocation_group_key: string
  updated_at: string | null
}

// Подмножество get_studio_plan_hints_v2 (только лиды/маркетинг — что показываем на шаге «Сколько»).
interface StudioPlanHints {
  plan_leads_week_avg: number | null
  fact_leads_week_avg: number | null
  fact_leads_3mo_avg: number | null
  plan_marketing_budget_week_avg: number | null
  fact_marketing_budget_week_avg: number | null
  fact_marketing_budget_3mo_avg: number | null
}

type Step = 'amount' | 'split' | 'confirm'
type UnitBasis = 'week' | 'remaining'

function roundToTwoDecimals(value: number) {
  return Math.round((value + Number.EPSILON) * 100) / 100
}

function buildAllocationGroupKey(studioIds: number[]) {
  return [...studioIds].sort((a, b) => a - b).join(',')
}

function formatCount(value: number) {
  if (Number.isInteger(value)) return value.toLocaleString('ru-RU')
  return value.toFixed(2).replace('.', ',')
}

function formatPercent(value: number) {
  return Number.isInteger(value) ? String(value) : value.toFixed(2).replace('.', ',')
}

function multiplyByWeeks(value: number, weeks: number) {
  return roundToTwoDecimals(value * weeks)
}

function formatWeeksLabel(weekNumbers: number[]) {
  if (weekNumbers.length === 0) return '—'
  const min = Math.min(...weekNumbers)
  const max = Math.max(...weekNumbers)
  return min === max ? `неделя ${min}` : `недели ${min}-${max}`
}

function toIsoDate(date: Date) {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function allocateProportionally(total: number, percentages: number[]): number[] {
  if (percentages.length === 0) return []
  let accumulated = 0
  return percentages.map((percent, index) => {
    if (index === percentages.length - 1) return roundToTwoDecimals(total - accumulated)
    const value = roundToTwoDecimals((total * percent) / 100)
    accumulated = roundToTwoDecimals(accumulated + value)
    return value
  })
}

// Пересчёт воронки от нового кол-ва лидов по сохранённым конверсиям/чекам (зеркало боевого
// computeFunnelRecalculation), дополнительно отдаёт дельты по ступеням для предпросмотра.
function computeFunnelRecalculation(existingRow: ExistingPlanRow | undefined, plannedLeadCount: number) {
  const previousLeadCount = Number(existingRow?.leads_count_plan) || 0
  const leadsChanged = Boolean(existingRow) && Math.abs(previousLeadCount - plannedLeadCount) > 0.005
  const empty = {
    fields: {} as Record<string, number>,
    previousGross: Number(existingRow?.gross_revenue_plan) || 0,
    newGross: Number(existingRow?.gross_revenue_plan) || 0,
    hasGrossChange: false,
    steps: [] as { label: string; from: number; to: number }[],
  }
  if (!leadsChanged) return empty

  const num = (v: number | null | undefined): number | null => (v === null || v === undefined ? null : Number(v))

  const bookingConversion = num(existingRow?.booking_conversion_plan)
  const appointmentsCount = bookingConversion !== null ? plannedLeadCount * (bookingConversion / 100) : null
  const appointmentsBase = appointmentsCount ?? num(existingRow?.appointments_count_plan) ?? 0

  const visitConversion = num(existingRow?.visit_conversion_plan)
  const newVisitorsCount = visitConversion !== null ? appointmentsBase * (visitConversion / 100) : null
  const visitorsBase = newVisitorsCount ?? num(existingRow?.new_visitors_count_plan) ?? 0

  const salesConversion = num(existingRow?.sales_conversion_plan)
  const newSalesCount = salesConversion !== null ? visitorsBase * (salesConversion / 100) : null
  const newSalesAov = num(existingRow?.new_sales_aov_plan)
  const newSalesAmount = newSalesCount !== null && newSalesAov !== null ? newSalesCount * newSalesAov : null

  const subscriptionsConversion = num(existingRow?.new_subscriptions_conversion_plan)
  const newSubscriptionsCount = subscriptionsConversion !== null ? visitorsBase * (subscriptionsConversion / 100) : null
  const newSubscriptionsAov = num(existingRow?.new_subscriptions_aov_plan)
  const newSubscriptionsAmount = newSubscriptionsCount !== null && newSubscriptionsAov !== null ? newSubscriptionsCount * newSubscriptionsAov : null

  const previousSubscriptionsCount = Number(existingRow?.new_subscriptions_count_plan) || 0
  const previousMembershipFees = Number(existingRow?.membership_fees_new_plan) || 0
  let membershipFeesNew: number | null = null
  if (newSubscriptionsCount !== null) {
    if (previousSubscriptionsCount > 0) membershipFeesNew = newSubscriptionsCount * (previousMembershipFees / previousSubscriptionsCount)
    else if (previousMembershipFees === 0) membershipFeesNew = 0
  }

  const fields: Record<string, number> = {}
  if (appointmentsCount !== null) fields.appointments_count_plan = roundToTwoDecimals(appointmentsCount)
  if (newVisitorsCount !== null) fields.new_visitors_count_plan = roundToTwoDecimals(newVisitorsCount)
  if (newSalesCount !== null) fields.new_sales_count_plan = roundToTwoDecimals(newSalesCount)
  if (newSalesAmount !== null) fields.new_sales_amount_plan = roundToTwoDecimals(newSalesAmount)
  if (newSubscriptionsCount !== null) fields.new_subscriptions_count_plan = roundToTwoDecimals(newSubscriptionsCount)
  if (newSubscriptionsAmount !== null) fields.new_subscriptions_amount_plan = roundToTwoDecimals(newSubscriptionsAmount)
  if (membershipFeesNew !== null) fields.membership_fees_new_plan = roundToTwoDecimals(membershipFeesNew)

  const previousGross = Number(existingRow?.gross_revenue_plan) || 0
  let hasGrossChange = false
  if (newSalesAmount !== null || newSubscriptionsAmount !== null || membershipFeesNew !== null) {
    const previousFunnelRevenue =
      (Number(existingRow?.new_sales_amount_plan) || 0) +
      (Number(existingRow?.new_subscriptions_amount_plan) || 0) +
      previousMembershipFees
    const funnelRevenue =
      (newSalesAmount ?? (Number(existingRow?.new_sales_amount_plan) || 0)) +
      (newSubscriptionsAmount ?? (Number(existingRow?.new_subscriptions_amount_plan) || 0)) +
      (membershipFeesNew ?? previousMembershipFees)
    fields.gross_revenue_plan = roundToTwoDecimals(previousGross - previousFunnelRevenue + funnelRevenue)
    hasGrossChange = true
  }

  const steps: { label: string; from: number; to: number }[] = []
  const pushStep = (label: string, to: number | null, fromKey: keyof ExistingPlanRow) => {
    if (to === null) return
    steps.push({ label, from: Number(existingRow?.[fromKey]) || 0, to: roundToTwoDecimals(to) })
  }
  pushStep('Записи на пробное', appointmentsCount, 'appointments_count_plan')
  pushStep('Приходы на пробное', newVisitorsCount, 'new_visitors_count_plan')
  pushStep('Продажи абонементов', newSalesCount, 'new_sales_count_plan')
  pushStep('Новые подписки', newSubscriptionsCount, 'new_subscriptions_count_plan')

  return { fields, previousGross, newGross: hasGrossChange ? fields.gross_revenue_plan : previousGross, hasGrossChange, steps }
}

function ReadonlyTotal({ value }: { value: string }) {
  return <div className="text-right font-semibold text-foreground">{value}</div>
}

function HintChips({ items }: { items: { label: string; value: string }[] }) {
  return (
    <div className="mt-1 flex flex-wrap gap-1 text-[10px] leading-none">
      {items.map((item) => (
        <span key={item.label} className="inline-flex items-center gap-1 rounded border border-border/70 bg-muted/60 px-1.5 py-1 text-foreground shadow-sm">
          <span className="font-medium text-muted-foreground">{item.label}</span>
          <span>{item.value}</span>
        </span>
      ))}
    </div>
  )
}

export function BulkLeadPlanGridModal({
  isOpen,
  onClose,
  onSaved,
  studios,
  currentPartnerNumber,
  currentPeriodId,
  currentYearNumber,
  currentMonthNumber,
  editableWeekNumbers,
  periodStart,
  periodLabel,
  userId,
  currency,
  today,
  periodLenDays,
}: Props) {
  const remainingWeeksCount = Math.max(editableWeekNumbers.length, 1)
  const weeksLabel = formatWeeksLabel(editableWeekNumbers)

  // Мини-гид по модалке: автозапуск при первом открытии + кнопка перезапуска в шапке.
  const tour = useOnboardingTour('bulk-leads-modal', isOpen)

  const [selectedCities, setSelectedCities] = useState<Set<number>>(new Set())
  const [percentages, setPercentages] = useState<Record<number, number | undefined>>({})
  const [totalLeads, setTotalLeads] = useState<number | undefined>()
  const [totalBudget, setTotalBudget] = useState<number | undefined>()
  const [step, setStep] = useState<Step>('amount')
  const [unitBasis, setUnitBasis] = useState<UnitBasis>('week')
  const [showWeeklyPreview, setShowWeeklyPreview] = useState(false)
  const [saving, setSaving] = useState(false)
  const [checkingExisting, setCheckingExisting] = useState(false)
  const [loadingHistory, setLoadingHistory] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [existingRows, setExistingRows] = useState<ExistingPlanRow[]>([])
  const [currentPlanRows, setCurrentPlanRows] = useState<ExistingPlanRow[]>([]) // #9: текущий план по неделям (для префилла и панели)
  // Подсказки/факт для шага «Сколько» — по всем студиям партнёра, суммируются для выбранных.
  const [studioHints, setStudioHints] = useState<Map<number, StudioPlanHints>>(new Map())
  const [studioPeriodFact, setStudioPeriodFact] = useState<Map<number, { leads: number; budget: number }>>(new Map())
  const [studioWeeklyFact, setStudioWeeklyFact] = useState<Map<string, { leads: number; budget: number }>>(new Map())

  const allCities = useMemo(() => {
    const map = new Map<number, string>()
    studios.forEach((studio) => {
      if (studio.city_id != null) map.set(studio.city_id, studio.city?.city_name || `Город ${studio.city_id}`)
    })
    return Array.from(map.entries()).map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name, 'ru'))
  }, [studios])

  const filteredStudios = useMemo(
    () => studios
      .filter((studio) => selectedCities.has(studio.city_id))
      .sort((a, b) => {
        const cityDiff = (a.city?.city_name ?? '').localeCompare(b.city?.city_name ?? '', 'ru')
        if (cityDiff !== 0) return cityDiff
        return (a.studio_number ?? '').localeCompare(b.studio_number ?? '', 'ru', { numeric: true })
      }),
    [selectedCities, studios]
  )

  const allocationGroupKey = useMemo(
    () => buildAllocationGroupKey(filteredStudios.map((s) => s.studio_internal_id)),
    [filteredStudios]
  )

  // #1: единый бюджет нельзя делить по студиям в разных валютах. Валюта набора берётся из
  // выбранных студий (а не из текущей — она может отличаться от выбранных городов).
  const selectedCurrencies = useMemo(
    () => Array.from(new Set(filteredStudios.map((s) => s.currency_code).filter((c): c is string => Boolean(c)))),
    [filteredStudios]
  )
  const currencyMismatch = selectedCurrencies.length > 1
  const effectiveCurrency = selectedCurrencies.length === 1 ? selectedCurrencies[0] : currency

  const totalPercent = useMemo(
    () => roundToTwoDecimals(filteredStudios.reduce((sum, s) => sum + (percentages[s.studio_internal_id] ?? 0), 0)),
    [filteredStudios, percentages]
  )
  const remainingPercent = roundToTwoDecimals(100 - totalPercent)
  const plannedTotalLeads = totalLeads ?? 0
  const plannedTotalBudget = totalBudget ?? 0
  const plannedLeadPrice = plannedTotalLeads > 0 ? roundToTwoDecimals(plannedTotalBudget / plannedTotalLeads) : 0

  const plannedRows = useMemo(() => {
    const withPct = filteredStudios.filter((s) => percentages[s.studio_internal_id] !== undefined)
    const allocatedLeads = allocateProportionally(totalLeads ?? 0, withPct.map((s) => percentages[s.studio_internal_id] ?? 0))
    const allocatedBudgets = allocateProportionally(totalBudget ?? 0, withPct.map((s) => percentages[s.studio_internal_id] ?? 0))
    return withPct.map((studio, index) => {
      const leadsCount = allocatedLeads[index] ?? 0
      const marketingBudget = allocatedBudgets[index] ?? 0
      return {
        studio,
        percent: percentages[studio.studio_internal_id] ?? 0,
        leadPrice: leadsCount > 0 ? roundToTwoDecimals(marketingBudget / leadsCount) : 0,
        leadsCount,
        marketingBudget,
      }
    })
  }, [filteredStudios, percentages, totalBudget, totalLeads])

  const plannedTotalBudgetForPeriod = multiplyByWeeks(plannedTotalBudget, remainingWeeksCount)
  const plannedTotalLeadsForPeriod = multiplyByWeeks(plannedTotalLeads, remainingWeeksCount)

  const isValid = Math.abs(totalPercent - 100) < 0.01 && totalLeads !== undefined && plannedRows.length > 0 && !currencyMismatch

  const hasExistingPlanForStudio = useMemo(() => {
    const map = new Map<number, boolean>()
    plannedRows.forEach(({ studio }) => {
      const hasExisting = existingRows.some((row) =>
        row.studio_internal_id === studio.studio_internal_id &&
        ((Number(row.cost_per_lead_plan) || 0) > 0 || (Number(row.leads_count_plan) || 0) > 0 || (Number(row.marketing_budget_plan) || 0) > 0)
      )
      map.set(studio.studio_internal_id, hasExisting)
    })
    return map
  }, [existingRows, plannedRows])

  const overwriteCount = useMemo(
    () => Array.from(hasExistingPlanForStudio.values()).filter(Boolean).length,
    [hasExistingPlanForStudio]
  )

  // #9: текущий план по неделям (сумма лидов/бюджета по выбранным студиям) — чтобы видеть «как в сетке
  // все недели». Неделя помечается отвязанной, если у любой выбранной студии источник ≠ partner_allocation.
  const currentWeeklyPanel = useMemo(() => {
    const selectedIds = new Set(filteredStudios.map((s) => s.studio_internal_id))
    return editableWeekNumbers.map((week) => {
      const rows = currentPlanRows.filter((r) => r.week_number_in_period === week && selectedIds.has(r.studio_internal_id))
      return {
        week,
        leads: rows.reduce((s, r) => s + (Number(r.leads_count_plan) || 0), 0),
        budget: rows.reduce((s, r) => s + (Number(r.marketing_budget_plan) || 0), 0),
        detached: rows.some((r) => r.plan_source && r.plan_source !== 'partner_allocation'),
        hasAny: rows.length > 0,
      }
    })
  }, [currentPlanRows, filteredStudios, editableWeekNumbers])
  const hasCurrentPlan = currentWeeklyPanel.some((c) => c.hasAny)
  const currentResetTotals = useMemo(() => {
    const referenceWeek = currentWeeklyPanel[0]
    return {
      leads: referenceWeek?.hasAny ? roundToTwoDecimals(referenceWeek.leads) : undefined,
      budget: referenceWeek?.hasAny ? roundToTwoDecimals(referenceWeek.budget) : undefined,
    }
  }, [currentWeeklyPanel])

  // Подсказки шага «Сколько»: сумма недельных средних (план / факт / факт-3мес) по выбранным студиям.
  const hintSum = useMemo(() => {
    let any = false
    const acc = { planLeads: 0, factLeads: 0, leads3mo: 0, planBudget: 0, factBudget: 0, budget3mo: 0 }
    filteredStudios.forEach((s) => {
      const h = studioHints.get(s.studio_internal_id)
      if (!h) return
      any = true
      acc.planLeads += Number(h.plan_leads_week_avg) || 0
      acc.factLeads += Number(h.fact_leads_week_avg) || 0
      acc.leads3mo += Number(h.fact_leads_3mo_avg) || 0
      acc.planBudget += Number(h.plan_marketing_budget_week_avg) || 0
      acc.factBudget += Number(h.fact_marketing_budget_week_avg) || 0
      acc.budget3mo += Number(h.fact_marketing_budget_3mo_avg) || 0
    })
    return any ? acc : null
  }, [filteredStudios, studioHints])

  // Факт периода к сегодня (сумма недельного факта по выбранным студиям) + прогноз по темпу.
  const factSum = useMemo(() => {
    let any = false, leads = 0, budget = 0
    filteredStudios.forEach((s) => {
      const f = studioPeriodFact.get(s.studio_internal_id)
      if (f) { any = true; leads += f.leads; budget += f.budget }
    })
    return any ? { leads, budget } : null
  }, [filteredStudios, studioPeriodFact])

  const periodStartedInModal = Boolean(periodStart) && Boolean(today) && today >= periodStart
  const periodTotalDays = periodLenDays > 0 ? periodLenDays : 28
  const observedDays = periodStartedInModal
    ? Math.min(Math.max(Math.floor((Date.parse(`${today}T00:00:00`) - Date.parse(`${periodStart}T00:00:00`)) / 86_400_000) + 1, 0), periodTotalDays)
    : 0
  const forecastFactor = observedDays > 0 ? periodTotalDays / observedDays : 1
  const forecastSum = factSum ? { leads: factSum.leads * forecastFactor, budget: factSum.budget * forecastFactor } : null
  const cplOf = (budget: number, leads: number) => (leads > 0 ? roundToTwoDecimals(budget / leads) : null)
  const periodWeeks = useMemo(() => {
    if (!periodStart) return []
    const start = new Date(`${periodStart}T00:00:00`)
    const periodEnd = new Date(start)
    periodEnd.setDate(periodEnd.getDate() + Math.max(periodTotalDays - 1, 0))
    const weekCount = Math.ceil(periodTotalDays / 7)
    const editableSet = new Set(editableWeekNumbers)
    return Array.from({ length: weekCount }, (_, index) => {
      const weekNumber = index + 1
      const weekStart = new Date(start)
      weekStart.setDate(start.getDate() + index * 7)
      const weekEnd = new Date(weekStart)
      weekEnd.setDate(weekStart.getDate() + 6)
      if (weekEnd > periodEnd) weekEnd.setTime(periodEnd.getTime())
      const weekStartIso = toIsoDate(weekStart)
      const weekEndIso = toIsoDate(weekEnd)
      return {
        weekNumber,
        weekStartIso,
        weekEndIso,
        isStarted: Boolean(today) && today >= weekStartIso,
        isCompleted: Boolean(today) && today > weekEndIso,
        isEditable: editableSet.has(weekNumber),
      }
    })
  }, [editableWeekNumbers, periodStart, periodTotalDays, today])

  const weeklyPlanFactGrid = useMemo(() => {
    const selectedIds = new Set(filteredStudios.map((s) => s.studio_internal_id))
    return periodWeeks.map((week) => {
      const currentRows = currentPlanRows.filter((row) =>
        row.week_number_in_period === week.weekNumber && selectedIds.has(row.studio_internal_id)
      )
      const currentPlan = {
        leads: currentRows.reduce((sum, row) => sum + (Number(row.leads_count_plan) || 0), 0),
        budget: currentRows.reduce((sum, row) => sum + (Number(row.marketing_budget_plan) || 0), 0),
      }
      let fact = { leads: 0, budget: 0 }
      selectedIds.forEach((studioId) => {
        const rowFact = studioWeeklyFact.get(`${studioId}:${week.weekNumber}`)
        if (!rowFact) return
        fact = {
          leads: fact.leads + rowFact.leads,
          budget: fact.budget + rowFact.budget,
        }
      })
      return {
        ...week,
        previousPlan: currentPlan,
        plan: {
          leads: week.isEditable && totalLeads !== undefined ? plannedTotalLeads : currentPlan.leads,
          budget: week.isEditable && totalBudget !== undefined ? plannedTotalBudget : currentPlan.budget,
        },
        fact,
      }
    })
  }, [currentPlanRows, filteredStudios, periodWeeks, plannedTotalBudget, plannedTotalLeads, studioWeeklyFact, totalBudget, totalLeads])

  const planFactTotals = useMemo(() => {
    const plan = weeklyPlanFactGrid.reduce((sum, week) => ({
      leads: sum.leads + week.plan.leads,
      budget: sum.budget + week.plan.budget,
    }), { leads: 0, budget: 0 })
    const fact = weeklyPlanFactGrid.filter((week) => week.isStarted).reduce((sum, week) => ({
      leads: sum.leads + week.fact.leads,
      budget: sum.budget + week.fact.budget,
    }), { leads: 0, budget: 0 })
    return {
      leads: {
        plan: roundToTwoDecimals(plan.leads),
        fact: roundToTwoDecimals(fact.leads),
        remaining: roundToTwoDecimals(plan.leads - fact.leads),
        forecast: forecastSum ? roundToTwoDecimals(forecastSum.leads) : null,
      },
      budget: {
        plan: roundToTwoDecimals(plan.budget),
        fact: roundToTwoDecimals(fact.budget),
        remaining: roundToTwoDecimals(plan.budget - fact.budget),
        forecast: forecastSum ? roundToTwoDecimals(forecastSum.budget) : null,
      },
    }
  }, [forecastSum, weeklyPlanFactGrid])

  // Сброс при открытии + предзаполнение % из сохранённого шаблона.
  useEffect(() => {
    if (!isOpen) return
    setSelectedCities(new Set(allCities.map((c) => c.id)))
    setPercentages({})
    setTotalLeads(undefined)
    setTotalBudget(undefined)
    setStep('amount')
    setUnitBasis('week')
    setShowWeeklyPreview(false)
    setExistingRows([])
    setCurrentPlanRows([])
    setStudioHints(new Map())
    setStudioPeriodFact(new Map())
    setStudioWeeklyFact(new Map())
    setError(null)
    setSuccess(false)
  }, [allCities, isOpen])

  // Подсказки (get_studio_plan_hints_v2) + факт периода (studio_weekly_plan_vs_fact) по ВСЕМ студиям
  // партнёра — на шаге «Сколько». Грузим для всего набора, суммируем реактивно по выбранным.
  useEffect(() => {
    const loadHintsAndFact = async () => {
      if (!isOpen || !currentPeriodId || studios.length === 0) return
      try {
        const supabase = getSupabase()
        const allIds = studios.map((s) => s.studio_internal_id)

        const factPromise = (supabase as any)
          .from('studio_weekly_plan_vs_fact')
          .select('studio_internal_id, week_number_in_period, weekly_leads_fact, weekly_marketing_expenses_amount_fact')
          .in('studio_internal_id', allIds)
          .eq('period_id', currentPeriodId)
        const hintsPromise = periodStart
          ? Promise.all(allIds.map((id) =>
              (supabase as any).rpc('get_studio_plan_hints_v2', { p_studio_internal_id: id, p_target_period_start: periodStart })
                .then((r: any) => [id, (r.data?.[0] ?? null) as StudioPlanHints | null] as const)
                .catch(() => [id, null] as const)
            ))
          : Promise.resolve([] as (readonly [number, StudioPlanHints | null])[])

        const [factRes, hintPairs] = await Promise.all([factPromise, hintsPromise])

        const factMap = new Map<number, { leads: number; budget: number }>()
        const weeklyFactMap = new Map<string, { leads: number; budget: number }>()
        ;(Array.isArray(factRes.data) ? factRes.data : []).forEach((r: any) => {
          const cur = factMap.get(r.studio_internal_id) ?? { leads: 0, budget: 0 }
          const leads = Number(r.weekly_leads_fact) || 0
          const budget = Math.abs(Number(r.weekly_marketing_expenses_amount_fact) || 0)
          cur.leads += leads
          cur.budget += budget
          factMap.set(r.studio_internal_id, cur)

          const weekNumber = Number(r.week_number_in_period) || 0
          if (weekNumber > 0) {
            const key = `${r.studio_internal_id}:${weekNumber}`
            const weekCur = weeklyFactMap.get(key) ?? { leads: 0, budget: 0 }
            weekCur.leads += leads
            weekCur.budget += budget
            weeklyFactMap.set(key, weekCur)
          }
        })
        setStudioPeriodFact(factMap)
        setStudioWeeklyFact(weeklyFactMap)

        const hintMap = new Map<number, StudioPlanHints>()
        hintPairs.forEach(([id, h]) => { if (h) hintMap.set(id, h) })
        setStudioHints(hintMap)
      } catch {
        // опционально — молча игнорируем
      }
    }
    loadHintsAndFact()
  }, [isOpen, currentPeriodId, periodStart, studios])

  // #9 (+#5): на открытии подтягиваем ТЕКУЩЕЕ состояние, чтобы балк не стартовал с нулей и показывал
  // недели по-отдельности. Грузим (а) текущий план по неделям (лиды/бюджет/источник) — для панели и
  // префилла тоталов; (б) шаблон % — последний по времени набор студий (восстанавливаем И города, И %).
  // % без шаблона — из фактической доли лидов текущего плана. Тоталы — сумма по reference-неделе.
  useEffect(() => {
    const loadCurrentState = async () => {
      if (!isOpen || !currentPartnerNumber || !currentPeriodId || !currentYearNumber || !currentMonthNumber || editableWeekNumbers.length === 0) return
      try {
        const supabase = getSupabase()
        const refWeek = Math.min(...editableWeekNumbers)
        const allIds = studios.map((s) => s.studio_internal_id)

        const [planRes, templateRes] = await Promise.all([
          (supabase as any)
            .from('studio_weekly_plan')
            .select('studio_internal_id, week_number_in_period, leads_count_plan, marketing_budget_plan, cost_per_lead_plan, plan_source')
            .eq('partner_number', currentPartnerNumber)
            .eq('year_number', currentYearNumber)
            .eq('month_number', currentMonthNumber)
            .in('studio_internal_id', allIds),
          (supabase as any)
            .from('partner_marketing_allocation_plan')
            .select('studio_internal_id, allocation_percent, is_enabled, allocation_group_key, updated_at')
            .eq('partner_number', currentPartnerNumber)
            .eq('period_id', currentPeriodId)
            .eq('week_number_in_period', refWeek),
        ])

        const planRows = (Array.isArray(planRes.data) ? planRes.data : []) as ExistingPlanRow[]
        setCurrentPlanRows(planRows)

        // (а) Восстановление набора студий + % из последнего шаблона (#5).
        const templateRows = ((Array.isArray(templateRes.data) ? templateRes.data : []) as AllocationTemplateRow[]).filter((r) => Boolean(r.is_enabled))
        let activeStudioIds: number[] = allIds
        let nextPercentages: Record<number, number | undefined> | null = null
        if (templateRows.length > 0) {
          const groups = new Map<string, { rows: AllocationTemplateRow[]; latest: number }>()
          templateRows.forEach((r) => {
            const g = groups.get(r.allocation_group_key) ?? { rows: [], latest: 0 }
            g.rows.push(r)
            const t = r.updated_at ? new Date(r.updated_at).getTime() : 0
            if (t > g.latest) g.latest = t
            groups.set(r.allocation_group_key, g)
          })
          let best: { rows: AllocationTemplateRow[]; latest: number } | null = null
          groups.forEach((g) => { if (!best || g.latest > best.latest) best = g })
          if (best) {
            const studioById = new Map(studios.map((s) => [s.studio_internal_id, s]))
            const cityIds = new Set<number>()
            const pct: Record<number, number | undefined> = {}
            best.rows.forEach((r) => {
              pct[r.studio_internal_id] = r.allocation_percent === null ? undefined : Number(r.allocation_percent)
              const st = studioById.get(r.studio_internal_id)
              if (st?.city_id != null) cityIds.add(st.city_id)
            })
            if (cityIds.size > 0) { setSelectedCities(cityIds); activeStudioIds = best.rows.map((r) => r.studio_internal_id) }
            nextPercentages = pct
          }
        }

        // reference-строки текущего плана для активного набора.
        const refRows = planRows.filter((r) => r.week_number_in_period === refWeek && activeStudioIds.includes(r.studio_internal_id))

        // (б) % из фактической доли лидов текущего плана, если шаблона нет.
        if (!nextPercentages) {
          const leadByStudio = new Map<number, number>()
          let leadTotal = 0
          refRows.forEach((r) => { const v = Number(r.leads_count_plan) || 0; leadByStudio.set(r.studio_internal_id, v); leadTotal += v })
          if (leadTotal > 0) {
            const raw = activeStudioIds.map((id) => ((leadByStudio.get(id) ?? 0) / leadTotal) * 100)
            const normalized = allocateProportionally(100, raw)
            const pct: Record<number, number | undefined> = {}
            activeStudioIds.forEach((id, i) => { pct[id] = normalized[i] })
            nextPercentages = pct
          }
        }
        if (nextPercentages) setPercentages(nextPercentages)

        // (в) Префилл тоталов — сумма по reference-неделе (убирает нули на открытии).
        const sumLeads = refRows.reduce((s, r) => s + (Number(r.leads_count_plan) || 0), 0)
        const sumBudget = refRows.reduce((s, r) => s + (Number(r.marketing_budget_plan) || 0), 0)
        if (sumLeads > 0) setTotalLeads(roundToTwoDecimals(sumLeads))
        if (sumBudget > 0) setTotalBudget(roundToTwoDecimals(sumBudget))
      } catch {
        // опционально — молча игнорируем
      }
    }
    loadCurrentState()
  }, [isOpen, currentPartnerNumber, currentPeriodId, currentYearNumber, currentMonthNumber, editableWeekNumbers, studios])

  const toggleCity = (cityId: number) => {
    setSelectedCities((prev) => {
      const next = new Set(prev)
      if (next.has(cityId)) next.delete(cityId)
      else next.add(cityId)
      return next
    })
    setPercentages((prev) => {
      const next = { ...prev }
      studios.forEach((s) => { if (s.city_id === cityId) delete next[s.studio_internal_id] })
      return next
    })
  }

  const distributeRemaining = () => {
    const unfilled = filteredStudios.filter((s) => percentages[s.studio_internal_id] === undefined)
    if (unfilled.length === 0 || remainingPercent <= 0) return
    setPercentages((prev) => {
      const next = { ...prev }
      const share = roundToTwoDecimals(remainingPercent / unfilled.length)
      let assigned = 0
      unfilled.forEach((studio, index) => {
        if (index === unfilled.length - 1) next[studio.studio_internal_id] = roundToTwoDecimals(remainingPercent - assigned)
        else { next[studio.studio_internal_id] = share; assigned += share }
      })
      return next
    })
  }

  // 🎯 Авто-% по исторической доле лидов (факт ~3 мес до периода).
  const applyHistoricalShares = async () => {
    if (filteredStudios.length === 0) return
    setLoadingHistory(true)
    setError(null)
    try {
      const supabase = getSupabase()
      const from = new Date(`${periodStart}T00:00:00`)
      from.setDate(from.getDate() - 90)
      const fromIso = `${from.getFullYear()}-${String(from.getMonth() + 1).padStart(2, '0')}-${String(from.getDate()).padStart(2, '0')}`
      const ids = filteredStudios.map((s) => s.studio_internal_id)
      const { data, error: histErr } = await supabase
        .from('studio_daily_totals')
        .select('studio_internal_id, leads_count')
        .in('studio_internal_id', ids)
        .gte('metric_date', fromIso)
        .lt('metric_date', periodStart)
      if (histErr) throw histErr
      const byStudio = new Map<number, number>()
      ;(data ?? []).forEach((r: any) => {
        byStudio.set(r.studio_internal_id, (byStudio.get(r.studio_internal_id) ?? 0) + (Number(r.leads_count) || 0))
      })
      const total = ids.reduce((s, id) => s + (byStudio.get(id) ?? 0), 0)
      if (total <= 0) {
        setError('Нет фактических лидов за прошлые ~3 мес для распределения по истории')
        return
      }
      // Доли через allocateProportionally (сумма ровно 100), порядок = filteredStudios.
      const rawShares = ids.map((id) => ((byStudio.get(id) ?? 0) / total) * 100)
      const normalized = allocateProportionally(100, rawShares)
      const next: Record<number, number | undefined> = {}
      ids.forEach((id, i) => { next[id] = normalized[i] })
      setPercentages(next)
    } catch (e: any) {
      setError(e?.message ?? 'Ошибка при загрузке истории лидов')
    } finally {
      setLoadingHistory(false)
    }
  }

  const handleProceedToConfirm = async () => {
    if (!isValid || !currentPartnerNumber || !currentYearNumber || !currentMonthNumber) return
    setCheckingExisting(true)
    setError(null)
    try {
      const supabase = getSupabase()
      const { data, error: existingError } = await (supabase as any)
        .from('studio_weekly_plan')
        .select(`
          studio_internal_id, week_number_in_period, cost_per_lead_plan, leads_count_plan, marketing_budget_plan,
          booking_conversion_plan, appointments_count_plan, visit_conversion_plan, new_visitors_count_plan,
          sales_conversion_plan, new_sales_count_plan, new_sales_aov_plan, new_sales_amount_plan,
          new_subscriptions_conversion_plan, new_subscriptions_count_plan, new_subscriptions_aov_plan,
          new_subscriptions_amount_plan, membership_fees_new_plan, gross_revenue_plan, plan_mode, plan_source
        `)
        .eq('partner_number', currentPartnerNumber)
        .eq('year_number', currentYearNumber)
        .eq('month_number', currentMonthNumber)
        .in('studio_internal_id', plannedRows.map((r) => r.studio.studio_internal_id))
        .in('week_number_in_period', editableWeekNumbers)
      if (existingError) throw existingError
      setExistingRows(Array.isArray(data) ? (data as ExistingPlanRow[]) : [])
      setStep('confirm')
    } catch (e: any) {
      setError(e?.message ?? 'Ошибка при проверке существующего плана')
    } finally {
      setCheckingExisting(false)
    }
  }

  const handleSave = async () => {
    if (!currentPartnerNumber || !currentYearNumber || !currentMonthNumber || editableWeekNumbers.length === 0) return
    setSaving(true)
    setError(null)
    try {
      const supabase = getSupabase()
      const nowIso = new Date().toISOString()
      const existingByKey = new Map<string, ExistingPlanRow>()
      existingRows.forEach((row) => existingByKey.set(`${row.studio_internal_id}:${row.week_number_in_period}`, row))

      const rowsToUpsert: Record<string, any>[] = []
      plannedRows.forEach((plannedRow) => {
        const studioId = plannedRow.studio.studio_internal_id
        editableWeekNumbers.forEach((weekNumber) => {
          const existingRow = existingByKey.get(`${studioId}:${weekNumber}`)
          const { fields: recalculated } = computeFunnelRecalculation(existingRow, plannedRow.leadsCount)
          rowsToUpsert.push({
            ...(existingRow ?? {}),
            studio_internal_id: studioId,
            partner_number: currentPartnerNumber,
            year_number: currentYearNumber,
            month_number: currentMonthNumber,
            week_number_in_period: weekNumber,
            cost_per_lead_plan: plannedRow.leadPrice,
            leads_count_plan: plannedRow.leadsCount,
            marketing_budget_plan: plannedRow.marketingBudget,
            ...recalculated,
            // Целевые недели заданы распределением маркетинга по студиям — отдельный источник
            // 'partner_allocation' (а не 'manual'), чтобы сетка подсвечивала «из распределения»
            // и ручная правка явно переводила ячейку в manual (бейдж пропадает = сигнал расхождения %).
            // #6: распределение — производный источник, не ручной ввод → plan_mode='auto',
            // manual_updated_* = null (инвариант «manual ⇔ правлено руками»). Кто/когда распределил
            // хранит partner_marketing_allocation_plan.updated_by_user_id.
            plan_mode: 'auto',
            plan_source: 'partner_allocation',
            manual_updated_at: null,
            manual_updated_by_user_id: null,
            updated_at: nowIso,
          })
        })
      })

      const { error: upsertError } = await (supabase as any)
        .from('studio_weekly_plan')
        .upsert(rowsToUpsert, { onConflict: 'studio_internal_id,year_number,month_number,week_number_in_period,partner_number' })
      if (upsertError) throw upsertError

      // Шаблон % для набора студий/периода — пишем на все оставшиеся недели (предзаполнение при след. открытии).
      if (currentPeriodId && allocationGroupKey && plannedRows.length > 0) {
        const allocationRows = editableWeekNumbers.flatMap((weekNumber) => plannedRows.map((plannedRow) => ({
          partner_number: currentPartnerNumber,
          period_id: currentPeriodId,
          week_number_in_period: weekNumber,
          allocation_group_key: allocationGroupKey,
          studio_internal_id: plannedRow.studio.studio_internal_id,
          allocation_percent: roundToTwoDecimals(plannedRow.percent),
          is_enabled: true,
          updated_by_user_id: userId ?? null,
          updated_at: nowIso,
        })))
        const { error: deleteTemplateError } = await (supabase as any)
          .from('partner_marketing_allocation_plan')
          .delete()
          .eq('partner_number', currentPartnerNumber)
          .eq('period_id', currentPeriodId)
          .eq('allocation_group_key', allocationGroupKey)
          .in('week_number_in_period', editableWeekNumbers)
        if (deleteTemplateError) throw deleteTemplateError
        const { error: insertTemplateError } = await (supabase as any)
          .from('partner_marketing_allocation_plan')
          .insert(allocationRows)
        if (insertTemplateError) throw insertTemplateError
      }

      setSuccess(true)
      onSaved?.()
      setTimeout(() => { setSuccess(false); setStep('amount'); onClose() }, 900)
    } catch (e: any) {
      setError(e?.message ?? 'Ошибка при сохранении плана')
    } finally {
      setSaving(false)
    }
  }

  const handleClose = () => { setStep('amount'); setError(null); setSuccess(false); onClose() }

  if (!isOpen) return null
  const fmtMoney = (value: number) => (effectiveCurrency ? formatCurrency(value, effectiveCurrency) : formatCount(value))

  // Единица ввода сумм: «за неделю» (каноническая) ⇄ «за оставшиеся недели». Состояние всегда
  // хранится по-недельно (totalLeads/totalBudget); тумблер лишь меняет, в какой шкале показываем поле.
  const basisInfo = unitBasis === 'week'
    ? { fieldSuffix: '/нед', otherLabel: `за ${weeksLabel}` }
    : { fieldSuffix: ` за ${weeksLabel}`, otherLabel: 'в неделю' }
  const leadsFieldValue = unitBasis === 'week' ? totalLeads : (totalLeads === undefined ? undefined : plannedTotalLeadsForPeriod)
  const budgetFieldValue = unitBasis === 'week' ? totalBudget : (totalBudget === undefined ? undefined : plannedTotalBudgetForPeriod)
  const setLeadsFromField = (v: number | undefined) => {
    if (unitBasis === 'week') setTotalLeads(v)
    else setTotalLeads(v === undefined ? undefined : (remainingWeeksCount > 0 ? roundToTwoDecimals(v / remainingWeeksCount) : v))
  }
  const setBudgetFromField = (v: number | undefined) => {
    if (unitBasis === 'week') setTotalBudget(v)
    else setTotalBudget(v === undefined ? undefined : (remainingWeeksCount > 0 ? roundToTwoDecimals(v / remainingWeeksCount) : v))
  }
  const amountCanReset = unitBasis !== 'week' || totalLeads !== currentResetTotals.leads || totalBudget !== currentResetTotals.budget
  const resetAmountDraft = () => {
    setUnitBasis('week')
    setTotalLeads(currentResetTotals.leads)
    setTotalBudget(currentResetTotals.budget)
    setError(null)
  }
  // Значения студий/итогов в выбранной шкале.
  const inBasis = (weekly: number) => unitBasis === 'week' ? weekly : multiplyByWeeks(weekly, remainingWeeksCount)

  const studiosValid = filteredStudios.length > 0 && !currencyMismatch
  const amountValid = totalLeads !== undefined && totalLeads > 0

  const wizardSteps: { key: Step; label: string }[] = [
    { key: 'amount', label: 'Ввод значений' },
    { key: 'split', label: 'Распределение' },
    { key: 'confirm', label: 'Сохранение изменений' },
  ]
  const activeStepIndex = wizardSteps.findIndex((s) => s.key === step)

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4">
      <div className="my-8 w-full max-w-4xl rounded-xl border border-border bg-card">
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <div>
            <h3 className="text-base font-medium">Распределить план лидов и маркетинга по студиям</h3>
            <div className="mt-1 text-xs text-muted-foreground">Период: {periodLabel || '—'} · применяется к {weeksLabel} · завершённые недели не трогаются</div>
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={tour.start}
              className="rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              title="Подсказка по модалке"
              aria-label="Показать подсказку по модалке"
            >
              <HelpCircle className="h-4 w-4" />
            </button>
            <button type="button" onClick={handleClose} className="rounded p-1 transition-colors hover:bg-muted" aria-label="Закрыть">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Степпер — линейный прогресс по шагам мастера. */}
        <div className="flex items-center gap-2 border-b border-border px-4 py-2.5" data-tour="leads-stepper">
          {wizardSteps.map((s, i) => {
            const done = i < activeStepIndex
            const active = i === activeStepIndex
            return (
              <div key={s.key} className="flex items-center gap-2">
                <div className={`flex items-center gap-1.5 ${active ? 'text-foreground' : done ? 'text-primary' : 'text-muted-foreground'}`}>
                  <span className={`flex h-5 w-5 items-center justify-center rounded-full text-[11px] font-semibold ${active ? 'bg-primary text-primary-foreground' : done ? 'bg-primary/15 text-primary' : 'bg-muted text-muted-foreground'}`}>
                    {done ? <Check className="h-3 w-3" /> : i + 1}
                  </span>
                  <span className="text-xs font-medium">{s.label}</span>
                </div>
                {i < wizardSteps.length - 1 && <div className={`h-px w-6 ${done ? 'bg-primary/40' : 'bg-border'}`} />}
              </div>
            )
          })}
        </div>

        <div className="space-y-4 p-4">
          {/* ── ШАГ 1 · СКОЛЬКО (лиды и бюджет на всю группу) ── */}
          {step === 'amount' && (
            <>
              <div className="rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-900">
                Введите общий план для выбранных студий франчайзи. Алгоритм сам пересчитает его в недельную
                ставку, разложит по долям (%) и сохранит только в оставшиеся недели ({weeksLabel}).
              </div>

              {/* Города — только если их больше одного (99% случаев: один город = вся сеть партнёра,
                  выбирать нечего). Тогда это просто фильтр, а не отдельный шаг. */}
              {allCities.length > 1 && (
                <div className="space-y-1.5" data-tour="leads-cities">
                  <div className="flex items-center justify-between text-xs">
                    <span className="font-medium text-muted-foreground">Города</span>
                    <span className="text-muted-foreground">Выбрано: {filteredStudios.length} {filteredStudios.length === 1 ? 'студия' : 'студий'}</span>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {allCities.map(({ id, name }) => (
                      <button key={id} type="button" onClick={() => toggleCity(id)}
                        className={`rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${selectedCities.has(id) ? 'border-primary bg-primary text-primary-foreground' : 'border-border bg-transparent text-muted-foreground hover:bg-muted'}`}>
                        {name}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="text-xs text-muted-foreground">
                На <span className="font-medium text-foreground">{filteredStudios.length} {filteredStudios.length === 1 ? 'студию' : 'студий'}</span> франчайзи · {weeksLabel}.
              </div>

              {currencyMismatch && (
                <div className="flex items-start gap-2 rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-800">
                  <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  <span>
                    Выбраны студии в разных валютах ({selectedCurrencies.join(', ')}). Единый бюджет нельзя
                    распределять по студиям разных валют — оставьте города одной валюты.
                  </span>
                </div>
              )}

              {/* Тумблер единицы ввода: один инпут, переключаемая база. */}
              <div className="flex flex-wrap items-center gap-2" data-tour="leads-basis">
                <span className="text-xs font-medium text-muted-foreground">Как вводить общий план:</span>
                <div className="inline-flex rounded-lg border border-border p-0.5 text-xs">
                  <button type="button" onClick={() => setUnitBasis('week')}
                    className={`rounded-md px-2.5 py-1 font-medium transition-colors ${unitBasis === 'week' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted'}`}>
                    за неделю
                  </button>
                  <button type="button" onClick={() => setUnitBasis('remaining')}
                    className={`rounded-md px-2.5 py-1 font-medium transition-colors ${unitBasis === 'remaining' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted'}`}>
                    за оставшиеся ({weeksLabel})
                  </button>
                </div>

                <button
                  type="button"
                  onClick={resetAmountDraft}
                  disabled={!amountCanReset}
                  className="ml-auto inline-flex items-center gap-1 rounded-lg border border-border px-2.5 py-1 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40"
                  title="Вернуть лиды и бюджет к текущему сохранённому плану выбранных студий"
                >
                  <RotateCcw className="h-3.5 w-3.5" />
                  Сбросить ввод
                </button>
              </div>

              <div className="grid gap-4 md:grid-cols-2" data-tour="leads-inputs">
                <div>
                  <div className="mb-1 text-sm font-medium text-foreground">Лиды: общий план {basisInfo.fieldSuffix}</div>
                  <NumberInput label="" value={leadsFieldValue} onChange={setLeadsFromField} inputMode="decimal" min={0} />
                  <div className="mt-1 text-xs text-muted-foreground">
                    Автопересчёт {basisInfo.otherLabel}: <span className="font-medium text-foreground">{formatCount(unitBasis === 'week' ? plannedTotalLeadsForPeriod : plannedTotalLeads)}</span>
                  </div>
                  {hintSum && (
                    <HintChips items={[
                      { label: 'План', value: formatCount(roundToTwoDecimals(hintSum.planLeads)) },
                      { label: 'Факт', value: formatCount(roundToTwoDecimals(hintSum.factLeads)) },
                      { label: '3 мес', value: formatCount(roundToTwoDecimals(hintSum.leads3mo)) },
                    ]} />
                  )}
                </div>
                <div>
                  <div className="mb-1 text-sm font-medium text-foreground">Маркетинг: общий бюджет {basisInfo.fieldSuffix}</div>
                  <NumberInput label="" value={budgetFieldValue} onChange={setBudgetFromField} inputMode="decimal" min={0} />
                  <div className="mt-1 text-xs text-muted-foreground">
                    Автопересчёт {basisInfo.otherLabel}: <span className="font-medium text-foreground">{fmtMoney(unitBasis === 'week' ? plannedTotalBudgetForPeriod : plannedTotalBudget)}</span>
                  </div>
                  {hintSum && (
                    <HintChips items={[
                      { label: 'План', value: fmtMoney(roundToTwoDecimals(hintSum.planBudget)) },
                      { label: 'Факт', value: fmtMoney(roundToTwoDecimals(hintSum.factBudget)) },
                      { label: '3 мес', value: fmtMoney(roundToTwoDecimals(hintSum.budget3mo)) },
                    ]} />
                  )}
                </div>
              </div>

              <div className="grid gap-2 md:grid-cols-3" data-tour="leads-summary">
                <div className="rounded-lg border border-border bg-muted/25 px-3 py-2">
                  <div className="text-xs text-muted-foreground">Будет сохранено в неделю</div>
                  <div className="mt-1 text-sm font-medium text-foreground">{formatCount(plannedTotalLeads)} лидов · {fmtMoney(plannedTotalBudget)}</div>
                </div>
                <div className="rounded-lg border border-border bg-muted/25 px-3 py-2">
                  <div className="text-xs text-muted-foreground">Итого за {weeksLabel}</div>
                  <div className="mt-1 text-sm font-medium text-foreground">{formatCount(plannedTotalLeadsForPeriod)} лидов · {fmtMoney(plannedTotalBudgetForPeriod)}</div>
                </div>
                <div className="rounded-lg border border-border bg-muted/25 px-3 py-2">
                  <div className="text-xs text-muted-foreground">Стоимость лида</div>
                  <div className="mt-1 text-sm font-medium text-foreground">{plannedTotalLeads > 0 ? fmtMoney(plannedLeadPrice) : '—'}</div>
                  <div className="mt-1 text-[11px] leading-snug text-muted-foreground">Бюджет ÷ лиды. Бюджет меняет стоимость лида, лиды меняют стоимость лида и воронку.</div>
                </div>
              </div>
              {hintSum && (cplOf(hintSum.planBudget, hintSum.planLeads) !== null || cplOf(hintSum.factBudget, hintSum.factLeads) !== null || cplOf(hintSum.budget3mo, hintSum.leads3mo) !== null) && (
                <div className="rounded-lg border border-border bg-muted/20 px-3 py-2">
                  <div className="text-xs font-medium text-muted-foreground">Историческая стоимость лида</div>
                  <HintChips items={[
                    { label: 'План', value: cplOf(hintSum.planBudget, hintSum.planLeads) !== null ? fmtMoney(cplOf(hintSum.planBudget, hintSum.planLeads)!) : '—' },
                    { label: 'Факт', value: cplOf(hintSum.factBudget, hintSum.factLeads) !== null ? fmtMoney(cplOf(hintSum.factBudget, hintSum.factLeads)!) : '—' },
                    { label: '3 мес', value: cplOf(hintSum.budget3mo, hintSum.leads3mo) !== null ? fmtMoney(cplOf(hintSum.budget3mo, hintSum.leads3mo)!) : '—' },
                  ]} />
                </div>
              )}

              {periodWeeks.length > 0 && (
                <div className="rounded-lg border border-border bg-muted/15">
                  <div className="border-b border-border px-3 py-2">
                    <div className="text-xs font-medium text-muted-foreground">План, факт и прогноз в формате сетки</div>
                    <div className="mt-0.5 text-[11px] leading-snug text-muted-foreground/75">
                      В редактируемых неделях показывается новый общий план из полей выше; в прошедших — сохранённый план и факт.
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[760px] border-collapse text-xs">
                      <thead>
                        <tr className="border-b border-border bg-muted/35 text-muted-foreground">
                          <th className="sticky left-0 z-10 bg-muted px-3 py-2 text-left font-medium" style={{ minWidth: 116 }}>Метрика</th>
                          {weeklyPlanFactGrid.map((week) => (
                            <th key={week.weekNumber} className="px-2 py-2 text-center font-medium" style={{ minWidth: 84 }}>
                              <div>Н{week.weekNumber}</div>
                              <div className="text-[10px] font-normal">{week.weekStartIso.slice(8, 10)}.{week.weekStartIso.slice(5, 7)}–{week.weekEndIso.slice(8, 10)}.{week.weekEndIso.slice(5, 7)}</div>
                              <span className={`mt-0.5 inline-block rounded-full px-1.5 py-0.5 text-[9px] font-semibold uppercase ring-1 ${
                                week.isCompleted
                                  ? 'bg-emerald-50 text-emerald-700 ring-emerald-200'
                                  : week.isStarted
                                    ? 'bg-amber-50 text-amber-700 ring-amber-200'
                                    : 'bg-slate-50 text-slate-600 ring-slate-200'
                              }`}>
                                {week.isCompleted ? 'прошла' : week.isStarted ? 'текущая' : 'будущая'}
                              </span>
                            </th>
                          ))}
                          {periodStartedInModal && <th className="border-l border-border px-3 py-2 text-center font-medium text-emerald-700" style={{ minWidth: 96 }}>Факт сегодня</th>}
                          {periodStartedInModal && <th className="px-3 py-2 text-center font-medium text-muted-foreground" style={{ minWidth: 104 }}>Остаток плана</th>}
                          <th className="border-l border-border bg-primary/5 px-3 py-2 text-center font-semibold text-primary" style={{ minWidth: 104 }}>Итоговый план</th>
                          {periodStartedInModal && <th className="border-l border-border bg-sky-50 px-3 py-2 text-center font-semibold text-sky-700" style={{ minWidth: 104 }}>Факт + прогноз</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {[
                          { key: 'leads' as const, label: 'Лиды', format: formatCount },
                          { key: 'budget' as const, label: 'Маркетинг', format: fmtMoney },
                        ].map((row) => (
                          <tr key={row.key} className="border-b border-border/60 last:border-0">
                            <td className="sticky left-0 z-10 bg-card px-3 py-2 font-medium">{row.label}</td>
                            {weeklyPlanFactGrid.map((week) => {
                              const plan = week.plan[row.key]
                              const previousPlan = week.previousPlan[row.key]
                              const fact = week.fact[row.key]
                              const showFact = week.isStarted
                              const changed = week.isEditable && roundToTwoDecimals(previousPlan) !== roundToTwoDecimals(plan)
                              return (
                                <td key={week.weekNumber} className={`px-2 py-2 text-center ${week.isEditable ? 'bg-primary/5' : ''}`}>
                                  {changed ? (
                                    <div className="inline-flex flex-col leading-tight">
                                      <span className="text-[10px] text-muted-foreground line-through">{row.format(previousPlan)}</span>
                                      <span className="font-semibold text-primary">{row.format(plan)}</span>
                                    </div>
                                  ) : (
                                    <div className="font-medium text-foreground">{row.format(plan)}</div>
                                  )}
                                  {showFact && (
                                    <div className="mt-0.5 text-[10px] leading-tight text-emerald-700">
                                      факт {row.format(fact)}
                                    </div>
                                  )}
                                </td>
                              )
                            })}
                            {periodStartedInModal && (
                              <td className="border-l border-border px-3 py-2 text-center font-medium text-emerald-700">
                                {row.format(planFactTotals[row.key].fact)}
                              </td>
                            )}
                            {periodStartedInModal && (
                              <td className="px-3 py-2 text-center text-muted-foreground">
                                {row.format(planFactTotals[row.key].remaining)}
                              </td>
                            )}
                            <td className="border-l border-border bg-primary/5 px-3 py-2 text-center font-semibold text-primary">
                              {row.format(planFactTotals[row.key].plan)}
                            </td>
                            {periodStartedInModal && (
                              <td className="border-l border-border bg-sky-50 px-3 py-2 text-center font-semibold text-sky-700">
                                {planFactTotals[row.key].forecast !== null ? row.format(planFactTotals[row.key].forecast) : '—'}
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {periodStartedInModal && factSum && (
                    <div className="border-t border-border px-3 py-2 text-[11px] leading-snug text-muted-foreground/75">
                      Прогноз по темпу: факт ÷ прошедшие дни ({observedDays}) × дни периода ({periodTotalDays}).
                    </div>
                  )}
                </div>
              )}

              {error && <p className="text-sm text-red-600">{error}</p>}

              <div className="flex gap-2 pt-1">
                <button type="button" onClick={handleClose} className="btn-secondary flex-1">Отмена</button>
                <button type="button" data-tour="leads-next" onClick={() => setStep('split')} className="btn-primary flex-1 disabled:cursor-not-allowed disabled:opacity-50" disabled={!amountValid || !studiosValid}>
                  Далее <ArrowRight className="ml-1 inline h-4 w-4" />
                </button>
              </div>
            </>
          )}

          {/* ── ШАГ 2 · КАК ДЕЛИМ (доли % по студиям) ── */}
          {step === 'split' && (
            <>
              <div className="flex items-center justify-between gap-3">
                <div className="text-xs text-muted-foreground">
                  Бюджет и лиды делятся одним %. Значения — <span className="font-medium text-foreground">{unitBasis === 'week' ? 'за неделю' : `за ${weeksLabel}`}</span>.
                </div>
                <div className="flex items-center gap-3">
                  <button type="button" onClick={applyHistoricalShares} disabled={loadingHistory}
                    className="flex items-center gap-1 text-xs text-primary transition-colors hover:text-primary/80 disabled:opacity-50">
                    <History className="h-3 w-3" />
                    {loadingHistory ? 'Загрузка…' : 'По исторической доле лидов'}
                  </button>
                  {filteredStudios.some((s) => percentages[s.studio_internal_id] === undefined) && remainingPercent > 0 && (
                    <button type="button" onClick={distributeRemaining} className="flex items-center gap-1 text-xs text-primary transition-colors hover:text-primary/80">
                      <Shuffle className="h-3 w-3" /> Распределить остаток ({formatPercent(remainingPercent)}%)
                    </button>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-[minmax(0,1.7fr)_90px_120px_140px] gap-2 px-1 text-[11px] font-medium uppercase tracking-[0.08em] text-muted-foreground">
                <div>Студия</div>
                <div className="text-right">%</div>
                <div className="text-right">Лиды</div>
                <div className="text-right">Бюджет</div>
              </div>

              <div className="space-y-2">
                {filteredStudios.map((studio) => {
                  const plannedRow = plannedRows.find((row) => row.studio.studio_internal_id === studio.studio_internal_id)
                  return (
                    <div key={studio.studio_internal_id} className="grid grid-cols-[minmax(0,1.7fr)_90px_120px_140px] items-center gap-2">
                      <div className="min-w-0">
                        <div className="truncate text-sm">{(studio.city?.city_name || 'Город')} · студия {studio.studio_number || studio.studio_internal_id}</div>
                        {studio.address && <div className="truncate text-xs text-muted-foreground">{studio.address}</div>}
                      </div>
                      <div>
                        <NumberInput label="" value={percentages[studio.studio_internal_id]}
                          onChange={(value) => setPercentages((prev) => ({ ...prev, [studio.studio_internal_id]: value }))}
                          inputMode="decimal" min={0} max={100} placeholder="%" />
                      </div>
                      <div className="text-right text-sm font-medium text-foreground">{plannedRow ? formatCount(inBasis(plannedRow.leadsCount)) : <span className="text-muted-foreground/40">—</span>}</div>
                      <div className="text-right text-sm font-medium text-foreground">{plannedRow ? fmtMoney(inBasis(plannedRow.marketingBudget)) : <span className="text-muted-foreground/40">—</span>}</div>
                    </div>
                  )
                })}
              </div>

              <div className="border-t border-border pt-3">
                <div className="grid grid-cols-[minmax(0,1.7fr)_90px_120px_140px] gap-2 text-sm">
                  <div className="font-medium text-muted-foreground">Итого</div>
                  <div className={`text-right font-semibold ${Math.abs(totalPercent - 100) < 0.01 ? 'text-green-600' : 'text-foreground'}`}>{formatPercent(totalPercent)}%</div>
                  <ReadonlyTotal value={formatCount(inBasis(plannedTotalLeads))} />
                  <ReadonlyTotal value={fmtMoney(inBasis(plannedTotalBudget))} />
                </div>
              </div>
              {error && <p className="text-sm text-red-600">{error}</p>}

              <div className="flex gap-2 pt-1">
                <button type="button" onClick={() => setStep('amount')} className="btn-secondary flex-1">
                  <ArrowLeft className="mr-1 inline h-4 w-4" /> Назад
                </button>
                <button type="button" onClick={handleProceedToConfirm} className="btn-primary flex-1 disabled:cursor-not-allowed disabled:opacity-50" disabled={!isValid || checkingExisting}>
                  {checkingExisting ? 'Проверка...' : <>Продолжить <ArrowRight className="ml-1 inline h-4 w-4" /></>}
                </button>
              </div>
            </>
          )}

          {step === 'confirm' && (
            <>
              {/* Понятное резюме: что именно сохранится и как это повлияет. */}
              <div className="space-y-2 rounded-lg border border-primary/30 bg-primary/5 p-3 text-sm">
                <div className="font-medium text-foreground">Проверьте перед сохранением</div>
                <ul className="space-y-1 text-xs text-muted-foreground">
                  <li>
                    План маркетинга и лидов задаётся сразу для <span className="font-medium text-foreground">{plannedRows.length} {plannedRows.length === 1 ? 'студии' : 'студий'}</span> франчайзи
                    по заданным долям&nbsp;(%).
                  </li>
                  <li>
                    Применяется к оставшимся неделям периода: <span className="font-medium text-foreground">{weeksLabel}</span> ({remainingWeeksCount} {remainingWeeksCount === 1 ? 'неделя' : 'нед.'}).
                    Завершённые недели не меняются.
                  </li>
                  <li>
                    На каждую оставшуюся неделю каждая студия получит одинаковый недельный план: <span className="font-medium text-foreground">{formatCount(plannedTotalLeads)} лидов</span> и
                    {' '}<span className="font-medium text-foreground">{fmtMoney(plannedTotalBudget)}</span> бюджета в неделю на всех
                    (за {weeksLabel}: {formatCount(plannedTotalLeadsForPeriod)} лидов · {fmtMoney(plannedTotalBudgetForPeriod)}).
                  </li>
                  <li>
                    {overwriteCount > 0
                      ? <>Обновится план у <span className="font-medium text-amber-700">{overwriteCount}</span> {overwriteCount === 1 ? 'студии' : 'студий'} (уже был план){plannedRows.length - overwriteCount > 0 ? <>, новый план — у <span className="font-medium text-foreground">{plannedRows.length - overwriteCount}</span></> : null}.</>
                      : <>Новый план будет создан для всех <span className="font-medium text-foreground">{plannedRows.length}</span> {plannedRows.length === 1 ? 'студии' : 'студий'} (раньше плана не было).</>}
                  </li>
                  <li>
                    Там, где меняется число лидов, пересчитаются воронка и плановая выручка (показано ниже). Продления,
                    апгрейды, индивидуальные и прочие доходы <span className="font-medium text-foreground">не трогаются</span>.
                  </li>
                </ul>
              </div>

              <div className={`rounded-lg border p-3 ${overwriteCount > 0 ? 'border-amber-300 bg-amber-50' : 'border-border bg-muted/40'}`}>
                <div className="mb-2 text-xs font-medium text-muted-foreground">Детали по студиям</div>
                {overwriteCount > 0 && (
                  <div className="mb-3 flex items-start gap-2 text-xs font-medium text-amber-800">
                    <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                    <span>Часть студий уже имеет сохранённый план. Лиды и бюджет будут обновлены на оставшихся неделях ({weeksLabel}). Воронка и плановая выручка пересчитаются только там, где изменилось количество лидов.</span>
                  </div>
                )}
                <div className="space-y-2">
                  {plannedRows.map((row) => {
                    // Предпросмотр по первой оставшейся неделе (ставка одинакова на всех).
                    const firstWeek = editableWeekNumbers[0]
                    const existingFirst = existingRows.find((e) => e.studio_internal_id === row.studio.studio_internal_id && e.week_number_in_period === firstWeek)
                    const funnel = computeFunnelRecalculation(existingFirst, row.leadsCount)
                    const prevLeads = Number(existingFirst?.leads_count_plan) || 0
                    const newLeads = row.leadsCount
                    const isNew = !existingFirst
                    const leadsChanged = !isNew && Math.abs(prevLeads - newLeads) > 0.005
                    return (
                      <div key={row.studio.studio_internal_id} className="space-y-1 border-b border-border/40 pb-2 last:border-0 last:pb-0">
                        <div className="flex items-start justify-between gap-3 text-sm">
                          <div className="min-w-0">
                            <div className="truncate">{(row.studio.city?.city_name || 'Город')} · студия {row.studio.studio_number || row.studio.studio_internal_id}</div>
                            <div className="truncate text-xs text-muted-foreground">{formatPercent(row.percent)}% · CPL {fmtMoney(row.leadPrice)}</div>
                          </div>
                          <div className="shrink-0 text-right">
                            <div className="font-medium text-foreground">{fmtMoney(row.marketingBudget)}/нед</div>
                            <div className="text-xs text-muted-foreground">за {weeksLabel}: {fmtMoney(multiplyByWeeks(row.marketingBudget, remainingWeeksCount))}</div>
                          </div>
                        </div>
                        {/* Лиды: явно «было → станет» (#8). */}
                        <div className="text-xs">
                          <span className="text-muted-foreground">Лиды/нед: </span>
                          {isNew ? (
                            <span className="font-medium text-foreground">{formatCount(newLeads)} (новый план)</span>
                          ) : (
                            <>
                              {formatCount(prevLeads)} <span className="font-medium text-foreground">→ {formatCount(newLeads)}</span>
                              {!leadsChanged && <span className="text-muted-foreground"> · без изменений</span>}
                            </>
                          )}
                          <span className="text-muted-foreground"> · за {weeksLabel}: {formatCount(multiplyByWeeks(newLeads, remainingWeeksCount))}</span>
                        </div>
                        {/* Воронка: ступени «было → станет» при изменении лидов; иначе — почему пересчёта нет. */}
                        {funnel.hasGrossChange ? (
                          <div className="text-xs text-muted-foreground">
                            {funnel.steps.map((s) => (
                              <div key={s.label}>{s.label}: {formatCount(s.from)} <span className="text-foreground">→ {formatCount(s.to)}</span></div>
                            ))}
                            <div>Плановая выручка/нед: {fmtMoney(funnel.previousGross)} <span className="text-foreground">→ {fmtMoney(funnel.newGross)}</span></div>
                          </div>
                        ) : (isNew || leadsChanged) ? (
                          <div className="text-xs text-muted-foreground/80">Воронка не задана — плановая выручка не пересчитывается.</div>
                        ) : null}
                      </div>
                    )
                  })}
                </div>
              </div>

              {error && <p className="text-sm text-red-600">{error}</p>}
              {success && <p className="text-sm text-green-600">План сохранён.</p>}

              <div className="flex gap-2 pt-1">
                <button type="button" onClick={() => { setStep('split'); setError(null) }} className="btn-secondary flex-1" disabled={saving}><ArrowLeft className="mr-1 inline h-4 w-4" /> Изменить</button>
                <button type="button" onClick={handleSave} className="btn-primary flex-1 disabled:cursor-not-allowed disabled:opacity-50" disabled={saving}>
                  {saving ? 'Сохранение...' : `Сохранить план · ${plannedRows.length} ${plannedRows.length === 1 ? 'студия' : 'студий'} · ${weeksLabel}`}
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      <OnboardingTour steps={BULK_LEADS_TOUR_STEPS} open={tour.open} onClose={tour.close} />
    </div>
  )
}
